import React, { useState, useEffect } from "react";
import useApi from "@/hooks/useApi";
import { ContractListItem, Contract, ContractStatus } from "./contractTypes";
import ContractEditor from "./ContractEditor";
import ContractDetail from "./ContractDetail";
import AmendmentEditor from "./AmendmentEditor";
import LoadingSpinner from "@/components/LoadingSpinner";

// ── Helpers ──────────────────────────────────────────────────────────────

function fmtCurrency(val: number) {
  return val.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function fmtDate(d?: string) {
  if (!d) return "\u2014";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function StatusBadge({ status }: { status: ContractStatus }) {
  const cfg: Record<ContractStatus, { label: string; cls: string }> = {
    draft: { label: "Draft", cls: "bg-slate-100 text-slate-600" },
    sent: { label: "Awaiting Signature", cls: "bg-blue-100 text-blue-700" },
    signed: { label: "Signed", cls: "bg-green-100 text-green-700" },
    void: { label: "Void", cls: "bg-red-100 text-red-600" },
  };
  const { label, cls } = cfg[status] ?? { label: status, cls: "bg-slate-100 text-slate-500" };
  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

// ── Contracts page ────────────────────────────────────────────────────────

export default function Contracts() {
  const api = useApi();
  const [contracts, setContracts] = useState<ContractListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [amendTarget, setAmendTarget] = useState<Contract | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [voiding, setVoiding] = useState<number | null>(null);
  const [sending, setSending] = useState<number | null>(null);
  const [upgradeModal, setUpgradeModal] = useState<{ contractId: number; currentValue: number } | null>(null);
  const [upgradeForm, setUpgradeForm] = useState({ newPlanName: "", newMonthlyValue: "", effectiveDate: "" });
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeResult, setUpgradeResult] = useState<{ amendmentId: number } | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  async function load() {
    try {
      const res = await api.admin.get("api/Contract");
      if (res.ok) {
        const data = await res.json();
        setContracts(Array.isArray(data) ? data : []);
      }
    } catch {
      setContracts([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleSend(id: number) {
    setSending(id);
    try {
      const res = await api.admin.post(`api/Contract/${id}/send`, {});
      if (res.ok) {
        const data = await res.json();
        const origin = window.location.origin;
        const token = data?.signingToken ?? id;
        const link = `${origin}/sign/${token}`;
        navigator.clipboard.writeText(link).catch(() => {});
        showToast("Signing link copied! " + link);
        await load();
      }
    } catch {
      // silent
    } finally {
      setSending(null);
    }
  }

  async function handleVoid(id: number) {
    if (!window.confirm("Void this contract? This cannot be undone.")) return;
    setVoiding(id);
    try {
      const res = await api.admin.post(`api/Contract/${id}/void`, {});
      if (res.ok) await load();
    } catch {
      // silent
    } finally {
      setVoiding(null);
    }
  }

  async function fetchForAmendment(id: number) {
    try {
      const res = await api.admin.get(`api/Contract/${id}`);
      if (res.ok) {
        const c = await res.json();
        setAmendTarget(c);
      }
    } catch {
      // silent
    }
  }

  function openUpgradeModal(c: ContractListItem) {
    setUpgradeForm({ newPlanName: "", newMonthlyValue: String(c.totalMonthlyValue ?? ""), effectiveDate: "" });
    setUpgradeResult(null);
    setUpgradeModal({ contractId: c.id, currentValue: c.totalMonthlyValue ?? 0 });
  }

  function closeUpgradeModal() {
    setUpgradeModal(null);
    setUpgradeResult(null);
    setUpgradeForm({ newPlanName: "", newMonthlyValue: "", effectiveDate: "" });
  }

  async function handleUpgrade() {
    if (!upgradeModal) return;
    const newVal = parseFloat(upgradeForm.newMonthlyValue);
    if (!upgradeForm.newPlanName.trim() || isNaN(newVal) || !upgradeForm.effectiveDate) {
      showToast("Please fill in all fields.");
      return;
    }
    setUpgrading(true);
    try {
      const res = await api.admin.post(`api/Contract/${upgradeModal.contractId}/generate-amendment`, {
        newPlanName: upgradeForm.newPlanName.trim(),
        newMonthlyValue: newVal,
        effectiveDate: upgradeForm.effectiveDate,
      });
      if (res.ok) {
        const data = await res.json();
        setUpgradeResult({ amendmentId: data.amendmentId ?? data.id ?? 0 });
        await load();
      } else {
        showToast("Failed to generate amendment. Please try again.");
      }
    } catch {
      showToast("Network error. Please try again.");
    } finally {
      setUpgrading(false);
    }
  }

  // ── Stats ─────────────────────────────────────────────────────────────
  const signedContracts = contracts.filter((c) => c.status === "signed");
  const pendingContracts = contracts.filter((c) => c.status === "sent");
  const draftContracts = contracts.filter((c) => c.status === "draft");
  const totalSignedValue = signedContracts.reduce((s, c) => s + (c.totalMonthlyValue ?? 0), 0);

  return (
    <div className="h-full overflow-y-auto">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 bg-slate-800 text-white text-sm px-4 py-3 rounded-lg shadow-lg z-[60] max-w-sm">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-800">Contracts</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage customer agreements and e-signatures</p>
        </div>
        <button
          onClick={() => setShowEditor(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary"
        >
          + New Contract
        </button>
      </div>

      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="text-sm text-slate-500 mb-1">Signed</div>
            <div className="text-2xl font-bold text-green-600">{signedContracts.length}</div>
            <div className="text-sm text-slate-500 mt-1">{fmtCurrency(totalSignedValue)}/mo total</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="text-sm text-slate-500 mb-1">Pending Signature</div>
            <div className="text-2xl font-bold text-blue-600">{pendingContracts.length}</div>
            <div className="text-sm text-slate-500 mt-1">
              {fmtCurrency(pendingContracts.reduce((s, c) => s + (c.totalMonthlyValue ?? 0), 0))}/mo
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="text-sm text-slate-500 mb-1">Drafts</div>
            <div className="text-2xl font-bold text-slate-600">{draftContracts.length}</div>
            <div className="text-sm text-slate-500 mt-1">In progress</div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : contracts.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <div className="text-5xl mb-3">&#128203;</div>
            <p className="font-medium text-lg">No contracts yet</p>
            <p className="text-sm mt-1">Create your first customer contract</p>
            <button
              onClick={() => setShowEditor(true)}
              className="mt-4 px-5 py-2.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary"
            >
              New Contract
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Customer</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Title</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Value</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Start</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Sent</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Signed</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {contracts.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{c.customerName}</div>
                      <div className="text-slate-400 text-xs">{c.customerEmail}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 max-w-xs truncate">{c.title}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-800">
                      {fmtCurrency(c.totalMonthlyValue)}
                      <span className="text-slate-400 font-normal text-xs">/mo</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <StatusBadge status={c.status} />
                        {(c.amendments?.length ?? 0) > 0 && (
                          <span className="text-xs text-slate-400">
                            {c.amendments.length} amendment{c.amendments.length !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(c.serviceStartDate)}</td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(c.sentAt)}</td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(c.signedAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <button
                          onClick={() => setDetailId(c.id)}
                          className="text-primary hover:underline text-xs font-medium"
                        >
                          View
                        </button>
                        {c.status === "draft" && (
                          <button
                            onClick={() => handleSend(c.id)}
                            disabled={sending === c.id}
                            className="text-blue-600 hover:underline text-xs font-medium disabled:opacity-50"
                          >
                            {sending === c.id ? "Sending..." : "Send"}
                          </button>
                        )}
                        {c.status === "signed" && (
                          <>
                            <button
                              onClick={() => fetchForAmendment(c.id)}
                              className="text-green-600 hover:underline text-xs font-medium"
                            >
                              Amendment
                            </button>
                            <button
                              onClick={() => openUpgradeModal(c)}
                              className="text-amber-600 hover:underline text-xs font-medium"
                            >
                              Upgrade
                            </button>
                          </>
                        )}
                        {(c.status === "draft" || c.status === "sent") && (
                          <button
                            onClick={() => handleVoid(c.id)}
                            disabled={voiding === c.id}
                            className="text-red-500 hover:underline text-xs font-medium disabled:opacity-50"
                          >
                            {voiding === c.id ? "..." : "Void"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showEditor && (
        <ContractEditor
          onClose={() => setShowEditor(false)}
          onCreated={load}
        />
      )}

      {detailId !== null && (
        <ContractDetail
          contractId={detailId}
          onClose={() => setDetailId(null)}
          onRefresh={load}
        />
      )}

      {amendTarget !== null && (
        <AmendmentEditor
          contract={amendTarget}
          onClose={() => setAmendTarget(null)}
          onSaved={load}
        />
      )}

      {upgradeModal !== null && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={closeUpgradeModal}>
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-semibold text-slate-800">Generate Upgrade Amendment</h2>
              <button onClick={closeUpgradeModal} className="text-slate-400 hover:text-slate-600 text-lg">&times;</button>
            </div>

            {upgradeResult ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-2xl mx-auto mb-3">&#10003;</div>
                <p className="font-semibold text-slate-800 mb-1">Amendment Draft Created</p>
                <p className="text-sm text-slate-500 mb-4">Amendment #{upgradeResult.amendmentId} has been generated and is ready to send.</p>
                <button
                  onClick={closeUpgradeModal}
                  className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary"
                >
                  Done
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-slate-500 mb-3">
                    Current value: <strong>{fmtCurrency(upgradeModal.currentValue)}/mo</strong>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">New Plan Name</label>
                  <input
                    type="text"
                    value={upgradeForm.newPlanName}
                    onChange={(e) => setUpgradeForm((f) => ({ ...f, newPlanName: e.target.value }))}
                    placeholder="e.g. Professional Plan"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">New Monthly Value ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={upgradeForm.newMonthlyValue}
                    onChange={(e) => setUpgradeForm((f) => ({ ...f, newMonthlyValue: e.target.value }))}
                    placeholder="0.00"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Effective Date</label>
                  <input
                    type="date"
                    value={upgradeForm.effectiveDate}
                    onChange={(e) => setUpgradeForm((f) => ({ ...f, effectiveDate: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={closeUpgradeModal}
                    className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpgrade}
                    disabled={upgrading}
                    className="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg disabled:opacity-50"
                  >
                    {upgrading ? "Generating..." : "Generate Amendment Draft"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
